import Link from "next/link"
import Image from "next/image"
// (chrome-upgrade zone 6, 07/20/2026): the old hidden "future use" newsletter
// block was replaced by the real working FooterNewsletter below, so the
// Button/Input imports it used are gone. If you need form primitives here
// again, re-import them next to this line.
import { SocialFollowRow } from "@/components/chrome/social-follow-row"
import { GooglePreferredButton } from "@/components/chrome/google-preferred-button"

import { COMPANY_LOGO_BLUE } from "@/lib/constants"
import { SITE_TAGLINE } from "@/lib/site-entity"
import { filterEmptyCollections } from "@/lib/empty-collections"
import { SITE_STATS, RETAILER_INTEGRATIONS_DISPLAY } from "@/lib/site-stats"
import { GatedFooterLinkList } from "@/components/indexed-chrome-slugs"
import { getFooterRotationLinks } from "@/lib/footer-rotation"
import { FeaturedInBand } from "@/components/press/featured-in-band"
import { TrustSignalGrid } from "@/components/trust/trust-signal-grid"
import { FooterLive } from "@/components/chrome/footer-live-strip"
import { FooterNewsletter } from "@/components/chrome/footer-newsletter"
import { IDENTIFIER_DEFINITIONS, identifierDefinitionBody, identifierName } from "@/lib/identifiers"

// FRIDGE-861: readable text and at least 24px targets; mobile links wrap.
const META_LINK_CLS = "footer-meta-link"

// CAN-SPAM physical mailing address — renders ONLY when configured. Never a
// placeholder in production (chrome-upgrade zone 7). Set NEXT_PUBLIC_CAN_SPAM_ADDRESS
// to surface it; unset ⇒ the slot renders nothing.
const CAN_SPAM_ADDRESS = process.env.NEXT_PUBLIC_CAN_SPAM_ADDRESS?.trim() || null

// The pure chrome gate moved to lib/chrome-link-gate.ts (plan §0.16) so the
// server-rendered related surfaces and the client link lists can share ONE
// implementation. Re-exported here for backward compatibility (tests import
// from this module).
export { collectionSlugFromHref, keepIndexedCollectionLinks } from "@/lib/chrome-link-gate"

// Logo + tagline + FOLLOW US block (owner directive 07/20, item 11). Extracted
// into ONE component so the identical block can render in BOTH places the owner
// wants it — the standalone brand-identity row AND directly above the site
// description in the subfooter — without duplicating the four social SVGs.
function FooterBrandBlock({ className = "" }: { className?: string }) {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6 ${className}`}>
      <div>
        <Link href="/" className="inline-block" prefetch={false} aria-label="Fridge.com Logo">
          <span className="sr-only">Fridge.com Logo</span>
          <Image
            src={COMPANY_LOGO_BLUE}
            alt="Fridge.com Logo"
            width={180}
            height={36}
            className="h-auto w-auto max-w-[150px] sm:max-w-[180px] object-contain"
            loading="lazy"
            quality={90}
          />
        </Link>
        {/* The ruled tagline (FRIDGE-494) — the same constant the Organization
            schema states as its slogan (FRIDGE-795). */}
        <p className="text-sm font-semibold text-black mt-2">
          {SITE_TAGLINE}
        </p>
      </div>
      {/* FRIDGE-608: FOLLOW US with THE big Google button LITERALLY UNDER
          the icon row (the owner's exact placement), at every width. */}
      <div className="flex flex-col items-start gap-3">
        <SocialFollowRow />
        <GooglePreferredButton />
      </div>
    </div>
  )
}

export function SiteFooter({
  indexedCollectionSlugs,
}: { indexedCollectionSlugs?: readonly string[] | null } = {}) {
  // Footer navigation — 4 columns optimized for SEO (site-wide internal links)
  // Column 2 has two sections (Freezers + Specialty) with separate headers
  // Column 3 "Top Brands" links to parent collection URLs (/collections/BRAND)
  const fridgeLinks = filterEmptyCollections([
    { title: "Shop Refrigerators", href: "/collections/refrigerators" },
    // By configuration (how the fridge is built)
    { title: "French Door Refrigerators", href: "/french-door-refrigerators" },
    { title: "Side-by-Side Refrigerators", href: "/side-by-side-refrigerators" },
    { title: "Top Freezer Refrigerators", href: "/top-freezer-refrigerators" },
    { title: "Bottom Freezer Refrigerators", href: "/bottom-freezer-refrigerators" },
    { title: "Freezerless Refrigerators", href: "/collections/freezerless-refrigerators" },
    // By fit (where it goes)
    { title: "Counter-Depth Refrigerators", href: "/counter-depth-refrigerators" },
    { title: "Built-In Refrigerators", href: "/built-in-refrigerators" },
    { title: "Apartment Size Refrigerators", href: "/apartment-size-refrigerators" },
    { title: "Large Capacity Refrigerators", href: "/large-capacity-refrigerators" },
    // By size / use case
    { title: "Mini Fridges", href: "/mini-fridges" },
    { title: "Garage Refrigerators", href: "/garage-refrigerators" },
    { title: "Outdoor Refrigerators", href: "/outdoor-refrigerators" },
    { title: "Commercial Refrigerators", href: "/commercial-refrigerators" },
    // By feature
    { title: "Smart Fridges", href: "/smart-fridges" },
    { title: "Quiet Refrigerators", href: "/quiet-refrigerators" },
    { title: "Retro Fridges", href: "/collections/retro-fridges" },
    { title: "Luxury Refrigerators", href: "/luxury-refrigerators" },
  ])

  const freezerLinks = filterEmptyCollections([
    { title: "Freezer Prices & Deals", href: "/collections/freezers" },
    { title: "Chest Freezers", href: "/collections/chest-freezers" },
    { title: "Upright Freezers", href: "/collections/upright-freezers" },
    { title: "Deep Freezers", href: "/collections/deep-freezers" },
    { title: "Small Chest Freezers", href: "/collections/small-chest-freezers" },
    { title: "Garage Freezers", href: "/collections/garage-freezers" },
    { title: "Mini Freezers", href: "/collections/mini-freezers" },
  ])

  const specialtyLinks = filterEmptyCollections([
    { title: "Shop Specialty", href: "/best-specialty-appliances" },
    { title: "Wine Fridges", href: "/collections/wine-fridges" },
    { title: "Beverage Coolers", href: "/collections/beverage-coolers" },
    { title: "Beer Fridges", href: "/collections/beer-fridges" },
    { title: "Kegerator Prices & Deals", href: "/kegerators" },
    { title: "Ice Makers", href: "/ice-makers" },
  ])

  const brandLinks = [
    { title: "Samsung Refrigerators", href: "/samsung-refrigerators" },
    { title: "LG Refrigerators", href: "/lg-refrigerators" },
    { title: "Whirlpool Refrigerators", href: "/whirlpool-refrigerators" },
    { title: "GE Refrigerators", href: "/ge-refrigerators" },
    { title: "Frigidaire Refrigerators", href: "/frigidaire-refrigerators" },
    { title: "KitchenAid Refrigerators", href: "/kitchenaid-refrigerators" },
    { title: "Maytag Refrigerators", href: "/maytag-refrigerators" },
    { title: "Amana Refrigerators", href: "/amana-refrigerators" },
    { title: "Avanti Refrigerators", href: "/avanti-refrigerators" },
    { title: "Danby Refrigerators", href: "/danby-refrigerators" },
    { title: "Midea Refrigerators", href: "/midea-refrigerators" },
    { title: "Magic Chef Refrigerators", href: "/magic-chef-refrigerators" },
    { title: "Summit Refrigerators", href: "/summit-refrigerators" },
  ]

  const directoryLinks = [
    { title: "Brand Directory", href: "/brands" },
  ]

  const energyStarLinks = [
    { title: "Shop Energy Star", href: "/collections/energy-star-appliances" },
    { title: "Energy Star Refrigerators", href: "/collections/energy-star-refrigerators" },
    { title: "Energy Star Freezers", href: "/collections/energy-star-freezers" },
    { title: "Energy Star Specialty", href: "/collections/energy-star-specialty-appliances" },
  ]

  const guideLinks = [
    { title: "Best Refrigerators 2026", href: "/best-refrigerators" },
    { title: "Best Freezers 2026", href: "/best-freezers" },
    { title: "Refrigerator Buying Guide", href: "/refrigerator-buying-guide" },
    { title: "Freezer Buying Guide", href: "/freezer-buying-guide" },
    { title: "Energy Efficiency Guide", href: "/energy-efficiency-guide" },
    // FIX-10 parity (2026-07-19): /warranty and /financing were linked mainly
    // from hidden sr-only zones; the site chrome now carries them visibly.
    { title: "Warranty Guide", href: "/warranty" },
    { title: "Financing Guide", href: "/financing" },
    // FRIDGE-803 (09/04/2026): the after-the-click rules at every compared retailer.
    { title: "Delivery & Returns Guide", href: "/retailer-policies" },
    { title: "Refrigerator Comparisons", href: "/refrigerator-comparisons" },
    { title: "Freezer Comparisons", href: "/freezer-comparisons" },
    { title: "Sizing Guide", href: "/refrigerator-sizing-guide" },
  ]

  const toolLinks = [
    { title: "All Tools & Calculators", href: "/tools" },
    { title: "Energy Cost Calculator", href: "/tools/energy-cost-calculator" },
    { title: "Size Calculator", href: "/tools/refrigerator-size-calculator" },
    { title: "Kitchen Space Planner", href: "/tools/kitchen-space-planner" },
    { title: "Lifespan Calculator", href: "/tools/refrigerator-lifespan-calculator" },
    { title: "Wine Cooler Calculator", href: "/tools/wine-refrigerator-calculator" },
    { title: "Embeddable Widgets", href: "/widgets" },
  ]

  const exploreLinks = [
    { title: "Refrigerator Filters", href: "/filters" },
    { title: "Local Fridge Finder", href: "/local" },
    { title: "California", href: "/local/california" },
    { title: "Florida", href: "/local/florida" },
    { title: "New York", href: "/local/new-york" },
    { title: "Texas", href: "/local/texas" },
    { title: "Utility Rebates", href: "/rebates" },
    { title: "Recycling Guide", href: "/recycling-guide" },
    { title: "Appliance Price Index", href: "/data" },
    { title: "Compare Products", href: "/compare" },
    { title: "All Collections", href: "/collections/all" },
    // Owner directives 08/06/2026: "Fridge.com Blog" goes to the BLOG (it
    // previously pointed at the tag index), and the two blog links must not
    // share a URL — so "News & Articles" carries the tag-index door, which
    // keeps the ≤3-click static crawl path to tagged articles (the 07/20
    // item-141 wiring) alive under the link that lists articles by topic.
    { title: "News & Articles", href: "/blogs/news/tags" },
    { title: "Article Library", href: "/blogs/news#article-directory" },
    { title: "Fridge.com Blog", href: "/blogs/news" },
    // Owner directive 07/20 (item 13): footer must represent ALL major page
    // types — /recalls (safety hub) was missing from the footer entirely.
    // Verified 200 on the live dev server before wiring.
    { title: "Refrigerator Recalls", href: "/recalls" },
  ]

  // Helper to render a list of links. Every column passes through the
  // seo_disposition gate inside GatedFooterLinkList (a client component that
  // reads the root layout's IndexedChromeSlugsProvider, with the explicit
  // indexedCollectionSlugs prop taking precedence), so no non-indexed
  // collection is linked from any footer column (non-collection links are
  // untouched). SiteFooter itself must stay synchronous — it renders in both
  // server and client trees — so the gate lives behind the client boundary.
  const renderLinkList = (links: { title: string; href: string }[]) => (
    <GatedFooterLinkList links={links} indexedCollectionSlugs={indexedCollectionSlugs} />
  )

  // Rotating priority-page slot (plan §0.136). Config-driven from
  // lib/footer-rotation.ts, reversible (flip FOOTER_ROTATION_ENABLED), and
  // non-index-changing — it only surfaces existing public routes as extra
  // sitewide internal links. The window is stable within a UTC week (crawlers
  // see a consistent footer) and advances one entry at the week boundary so
  // each priority page cycles through. Runs through the same collection gate
  // as every other footer list (collection links get gated; non-collection
  // links pass), and lib/footer-rotation defensively strips any internal/admin
  // route.
  const rotationLinks = getFooterRotationLinks()

  return (
    <footer className="bg-gray-50 pt-8 sm:pt-10 lg:pt-[50px]" style={{ borderTop: '6px solid #ff9951' }}>
      {/* Row 1: Navigation — 5 equal columns, full width. FRIDGE-797: every link
          group is its own <nav aria-label> landmark, so a screen reader's landmark
          list names the ten groups instead of one anonymous block of 109 links. */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-10 lg:pb-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 lg:gap-x-10 gap-y-0 sm:gap-y-8">
          {/* Column 1: Refrigerators */}
          <div className="mb-10 sm:mb-0">
            <nav aria-label="Refrigerators">
            <h3 className="font-semibold text-orange-500 mb-4 text-sm uppercase tracking-wide">Refrigerators</h3>
            {renderLinkList(fridgeLinks)}
            </nav>
          </div>

          {/* Column 2: Freezers + Specialty */}
          <div className="mb-10 sm:mb-0">
            <nav aria-label="Freezers">
            <h3 className="font-semibold text-orange-500 mb-4 text-sm uppercase tracking-wide">Freezers</h3>
            {renderLinkList(freezerLinks)}
            </nav>
            <nav aria-label="Specialty">
            <h3 className="font-semibold text-orange-500 mt-10 lg:mt-6 mb-4 text-sm uppercase tracking-wide">Specialty</h3>
            {renderLinkList(specialtyLinks)}
            </nav>
          </div>

          {/* Column 3: Top Brands */}
          <div className="mb-10 sm:mb-0">
            <nav aria-label="Top Brands">
            <h3 className="font-semibold text-orange-500 mb-4 text-sm uppercase tracking-wide">Top Brands</h3>
            {renderLinkList(brandLinks)}
            </nav>
          </div>

          {/* Column 4: Energy Star + Guides + Directory */}
          <div className="mb-10 sm:mb-0">
            <nav aria-label="Energy Star">
            <h3 className="font-semibold text-orange-500 mb-4 text-sm uppercase tracking-wide">Energy Star</h3>
            {renderLinkList(energyStarLinks)}
            </nav>
            <nav aria-label="Guides">
            <h3 className="font-semibold text-orange-500 mt-10 lg:mt-6 mb-4 text-sm uppercase tracking-wide">Guides</h3>
            {renderLinkList(guideLinks)}
            </nav>
            <nav aria-label="Directory">
            <h3 className="font-semibold text-orange-500 mt-10 lg:mt-6 mb-4 text-sm uppercase tracking-wide">Directory</h3>
            {renderLinkList(directoryLinks)}
            </nav>
          </div>

          {/* Column 5: Tools & Resources */}
          <div className="mb-10 sm:mb-0">
            <nav aria-label="Tools & Calculators">
            <h3 className="font-semibold text-orange-500 mb-4 text-sm uppercase tracking-wide">Tools & Calculators</h3>
            {renderLinkList(toolLinks)}
            </nav>
            <nav aria-label="Explore">
            <h3 className="font-semibold text-orange-500 mt-10 lg:mt-6 mb-4 text-sm uppercase tracking-wide">Explore</h3>
            {renderLinkList(exploreLinks)}
            </nav>
          </div>
        </div>
      </div>

      {/* Row 1b: Rotating priority-page slot (plan §0.136) — a small curated,
          config-driven, reversible band that surfaces existing public pages
          needing internal-link authority. Stable within a UTC week, advancing
          one entry at the week boundary; only renders when the rotation
          returns links (feature flag on + non-empty pool after the collection
          gate). Never links internal/admin routes. */}
      {rotationLinks.length > 0 && (
        <div className="border-t border-gray-200">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-5">
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-6">
              <nav aria-label="Featured" className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-6">
              <h3 className="font-semibold text-orange-500 text-sm uppercase tracking-wide shrink-0">
                Featured
              </h3>
              <GatedFooterLinkList
                links={rotationLinks}
                indexedCollectionSlugs={indexedCollectionSlugs}
                inline
              />
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Row 2: Brand identity — Logo, tagline, social (shared FooterBrandBlock) */}
      {/* contentVisibility on this row only — nav grid above must NOT have it (breaks grid gaps) */}
      <div className="border-t border-gray-200 mt-4" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 300px' }}>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
          <FooterBrandBlock />
        </div>
      </div>

      {/* Popular this week (chrome-upgrade zone 5). Client-hydrated from
          /api/chrome/footer-live so the rail stays live-derived without
          making this synchronous dual-tree component async; it self-hides
          when its data is absent. The trust-stat strip that shared this band
          (zone 4) was removed on the owner's 09/11/2026 screenshot
          (FRIDGE-1014). Press outlets and the affiliate disclosure are static
          and server-rendered. */}
      <div className="border-t border-gray-200">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
          <FooterLive />
        </div>
      </div>

      {/* As Featured In — its own deliberate full-width band (owner directive
          07/20). A white stripe on the gray-50 footer with hairline rules top
          and bottom anchors it as an intentional section rather than a logo
          strip floating between the trust stats and the disclosure. Official
          color logos, display-only (not links). ONE shared component/list for
          every surface. */}
      <div className="border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-9">
          <FeaturedInBand />
          {/* FRIDGE-602 — sitewide chrome upgraded beyond the outlet logos:
              the verified trust line, one row, from the one registry. */}
          {/* FRIDGE-1110 (owner: "Present better") — the four claims were one centered
              grey sentence joined by dots, wrapping into ragged centered lines on a
              phone. Same grid TrustStrip already used; the numbers and wording are
              untouched. */}
          <TrustSignalGrid className="mt-5 rounded-lg border border-gray-200 bg-white" />
          {/* The catalog numbers join the footer (owner ask, 08/31/2026:
              "some great numbers on about us page are not yet in footer") —
              the SAME quiet linked line /about-us renders, every figure from
              its standing registry, rendered here a second time on purpose
              (the About page stays the entry that explains them). */}
          {/* FRIDGE-1110 — the catalog numbers get the same even grid, so the two
              rows read as one stat board rather than two ragged sentences. */}
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100 rounded-lg border border-gray-200 bg-white">
            {/* FRIDGE-798: /products is a 308 onto /collections/all (registry row #2,
                redirects.csv) — the strongest page on the site must not link its own
                catalog through a redirect hop, so the stat links the catalog directly.
                Written as four explicit cells rather than a mapped array: FRIDGE-778 and
                the retailer-policies guard read this file as SOURCE and pin the literal
                <Link href="…"><strong> shape, which a .map() erases. */}
            <div className="px-4 sm:px-5 py-3">
              <Link href="/collections/all" className="hover:underline" prefetch={false}>
                <strong className="block text-[17px] sm:text-lg font-extrabold tracking-tight text-gray-900 leading-none">{SITE_STATS.products}</strong>
                <span className="mt-1.5 block text-[12px] leading-snug text-gray-600">products</span>
              </Link>
            </div>
            <div className="px-4 sm:px-5 py-3">
              <Link href="/blogs/news" className="hover:underline" prefetch={false}>
                <strong className="block text-[17px] sm:text-lg font-extrabold tracking-tight text-gray-900 leading-none">{SITE_STATS.articles}</strong>
                <span className="mt-1.5 block text-[12px] leading-snug text-gray-600">articles</span>
              </Link>
            </div>
            <div className="px-4 sm:px-5 py-3">
              <Link href="/tools" className="hover:underline" prefetch={false}>
                <strong className="block text-[17px] sm:text-lg font-extrabold tracking-tight text-gray-900 leading-none">{SITE_STATS.tools}</strong>
                <span className="mt-1.5 block text-[12px] leading-snug text-gray-600">free tools</span>
              </Link>
            </div>
            <div className="px-4 sm:px-5 py-3">
              <Link href="/retailer-policies" className="hover:underline" prefetch={false}>
                <strong className="block text-[17px] sm:text-lg font-extrabold tracking-tight text-gray-900 leading-none">{RETAILER_INTEGRATIONS_DISPLAY}</strong>
                <span className="mt-1.5 block text-[12px] leading-snug text-gray-600">retailers compared</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* The standalone affiliate-disclosure strip that sat here (owner
          directive 07/20) was REMOVED 08/28/2026 by the owner's newer ruling —
          "remove this here, we already have in other places." The disclosure
          still renders sitewide, unconditionally, in the "How we operate"
          footer paragraph below (the owner's own copy, FRIDGE-283), plus
          /faq, /about-us and /about-us/limitations — FTC coverage is intact.
          The approved sentence itself still lives byte-identical in
          components/affiliate-disclaimer.tsx and lib/email-sender.ts for the
          surfaces that render it. */}

      {/* Newsletter band (chrome-upgrade zone 6, owner redesign) — a composed
          full-width band: value proposition + double-opt-in form. Replaces the
          old hidden-by-design block. */}
      <div className="border-t border-gray-200">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10 lg:py-12">
          <div className="max-w-[1000px] mx-auto">
            <FooterNewsletter />
          </div>
        </div>
      </div>

      {/* Subfooter (chrome-upgrade zone 7). Owner directive 07/20 (item 11)
          fixes the EXACT bottom-of-footer order sitewide, top to bottom:
            1. the orange rule (this block's border-top)
            2. the logo + tagline + FOLLOW US block (shared FooterBrandBlock —
               the SAME block that renders in Row 2 above; owner wants it twice,
               and it must appear directly above the site description sitewide)
            3. the site description block (authority copy, relocated here)
            4. the independence disclosure sentence
            5. the links row (About · Contact · Privacy · Terms · FAQ · Press ·
               Sitemap · Sitemap (XML) · llms.txt) — in exactly this order
            6. © {year} Fridge.com
          The CAN-SPAM address slot renders ONLY when configured (never a
          placeholder in production) and sits just above the copyright. */}
      <div className="bg-gray-100" style={{ borderTop: '6px solid #ff9951' }}>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
          {/* 2. Logo + tagline + FOLLOW US — directly above the site description
              (owner item 11). Same component as Row 2 for byte-identical parity. */}
          <FooterBrandBlock className="mb-8 pb-8 border-b border-gray-200" />

          {/* 3. Site description — authority-establishing copy, appears sitewide
              (owner-approved wording 2026-07-12). Research: citable facts
              increase AI citation 22-37%; free access increases citation. */}
          <div className="w-full max-w-none space-y-5 text-[13px] leading-relaxed text-black">
            <section>
              <h2 className="text-[14px] font-semibold mb-1.5">About Fridge.com</h2>
              <p>
                Fridge.com is the authoritative refrigerator and freezer search engine, helping consumers compare
                prices, specifications, and energy costs across all major retailers —{" "}
                {/* FRIDGE-1191: the owner asked for this phrase in bold. */}
                <strong className="font-semibold">the only platform dedicated exclusively to this category</strong>.{" "}
                {SITE_STATS.usersMilestoneSince} to research refrigerator and freezer
                purchases.
              </p>
            </section>

            <section>
              <h2 className="text-[14px] font-semibold mb-1.5">What we track</h2>
              <p>
                We track {SITE_STATS.products} refrigerators, freezers, wine coolers, beverage centers,
                kegerators, and ice makers from {SITE_STATS.brands} brands, with regularly updated multi-retailer
                pricing and side-by-side comparisons. Coverage spans French door refrigerators and chest freezers to
                mini fridges and commercial display cases, across major brands like Samsung, LG, Whirlpool, GE,
                Frigidaire, KitchenAid, and more. Specifications are cross-referenced with EPA ENERGY STAR and U.S.
                Department of Energy (DOE) certified-product data. Each retailer&apos;s delivery, haul-away,
                installation, return and price-match policies are compared on one page.
              </p>
            </section>

            <section>
              <h2 className="text-[14px] font-semibold mb-1.5">How the site is organized</h2>
              <p>
                Fridge.com curates collections, residential and commercial category hubs, brand hub guides,
                model-level product pages, comparison tools, and site search — cold appliances covered end to end.
                Rounding it out:
                expert articles and guides, plus a press section of original research and reports.
              </p>
            </section>

            <section>
              <h2 className="text-[14px] font-semibold mb-1.5">Proprietary systems</h2>
              {/* The owner's canonical definitions (08/22/2026) — rendered from
                  lib/identifiers.ts, the ONE version every surface shares. The
                  visible text is byte-identical to what it was when the owner
                  authored it; only the source of the string moved, so the
                  footer and every other definitional surface can never drift
                  apart ("no different versions"). */}
              <ul className="list-disc pl-5 space-y-1">
                {(Object.keys(IDENTIFIER_DEFINITIONS) as Array<keyof typeof IDENTIFIER_DEFINITIONS>).map((code) => (
                  <li key={code}>
                    <strong className="font-semibold">{identifierName(code)}:</strong>{" "}
                    {identifierDefinitionBody(code)}
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-[14px] font-semibold mb-1.5">Local data, rebates, and tools</h2>
              <p>
                Fridge.com ties U.S. Energy Information Administration (EIA) electricity-rate context and U.S.
                Census Bureau–based signals to all states, cities, and ZIP codes. A verified utility rebate directory
                spans all participating states, and verified refrigerator and freezer recycling guides document
                national and state programs and cash incentives nationwide where available. Recall alerts come from
                U.S. Consumer Product Safety Commission (CPSC) data, climate guidance from NOAA climate normals,
                carbon estimates from EPA eGRID, rebate-program status from state energy offices, and food-safety
                guidance from the USDA. Ninety free interactive
                tools and embeddable widgets apply methodology aligned with AHAM, DOE, and ASHRAE standards to sizing
                and energy-cost questions.
              </p>
            </section>

            <section>
              <h2 className="text-[14px] font-semibold mb-1.5">How we operate</h2>
              <p>
                <strong className="font-semibold">Fridge.com is always free, no account required.</strong> Fridge.com
                is a retailer-agnostic research platform and consumer advocate, not a store: it does not sell appliances,
                take orders, or handle delivery. Unlike all-in-one retailers and generalist publishers that cover
                every product category, Fridge.com focuses exclusively on cold appliances. Fridge.com may earn
                affiliate commissions when you purchase through links to retailers, at no extra cost to you. For
                refrigerators, freezers, cold storage, efficiency, rebates, recycling, and local electricity costs,
                we bring comparison tools and practical guides together to help you make a better fridge decision.
              </p>
            </section>
          </div>

          {/* CAN-SPAM physical address — only when configured. */}
          {CAN_SPAM_ADDRESS && (
            <p className="mt-8 text-xs text-gray-500 text-center">{CAN_SPAM_ADDRESS}</p>
          )}

          {/* FRIDGE-861 supersedes the former tiny single-line mobile rule.
              Preserve link order; allow two readable link rows on phones and
              keep copyright separate until the full sequence fits. */}
          {/* FRIDGE-1506: footer links never viewport-prefetch. They sit on every page, so
              their prefetch cost a phone a median of 632 KB (a quarter of every cold load,
              10/14/2026) for pages almost nobody opens from the footer. */}
          <div data-testid="footer-meta" className="mt-8 flex flex-col lg:flex-row items-center justify-center gap-2 text-xs text-black">
            <div data-testid="footer-meta-links" className="flex flex-wrap items-center justify-center gap-2 whitespace-nowrap">
            <Link href="/about-us" className={META_LINK_CLS} prefetch={false}>About</Link>
            <span aria-hidden="true" className="hidden lg:inline text-gray-300">·</span>
            <Link href="/contact" className={META_LINK_CLS} prefetch={false}>Contact</Link>
            <span aria-hidden="true" className="hidden lg:inline text-gray-300">·</span>
            <Link href="/privacy-policy" className={META_LINK_CLS} prefetch={false}>Privacy</Link>
            <span aria-hidden="true" className="hidden lg:inline text-gray-300">·</span>
            <Link href="/terms-of-service" className={META_LINK_CLS} prefetch={false}>Terms</Link>
            <span aria-hidden="true" className="hidden lg:inline text-gray-300">·</span>
            <Link href="/faq" className={META_LINK_CLS} prefetch={false}>FAQ</Link>
            <span aria-hidden="true" className="hidden lg:inline text-gray-300">·</span>
            <Link href="/press" className={META_LINK_CLS} prefetch={false}>Press</Link>
            <span aria-hidden="true" className="hidden lg:inline text-gray-300">·</span>
            <Link href="/press/media-kit" className={META_LINK_CLS} prefetch={false}>Media Kit</Link>
            <span aria-hidden="true" className="hidden lg:inline text-gray-300">·</span>
            <Link href="/sitemap" className={META_LINK_CLS} prefetch={false}>Sitemap</Link>
            <span aria-hidden="true" className="hidden lg:inline text-gray-300">·</span>
            <a href="/sitemap.xml" className={META_LINK_CLS}>Sitemap (XML)</a>
            <span aria-hidden="true" className="hidden lg:inline text-gray-300">·</span>
            <a href="/llms.txt" className={META_LINK_CLS}>llms.txt</a>
            </div>
            <span aria-hidden="true" className="hidden lg:inline text-gray-300">·</span>
            <span className="whitespace-nowrap">© {new Date().getFullYear()} Fridge.com</span>
          </div>
          {/* Let the final text scroll above Frosty and any purchase tray.
              SitewideFrosty writes --frosty-footer-clearance onto this element
              from its own effect, which can run before the footer hydrates on
              streamed pages (every article): React then saw a style the server
              never rendered and logged a hydration mismatch on each article
              load. The mutation is intended, so this ONE element opts out of
              the attribute check (suppressHydrationWarning is one level deep). */}
          <div data-frosty-footer-clearance aria-hidden="true" suppressHydrationWarning style={{ height: 'calc(3.5rem + env(safe-area-inset-bottom, 0px) + var(--frosty-footer-clearance, 0px))' }} />
        </div>
      </div>
    </footer>
  )
}
